import React from 'react'

export interface ReportBreakdownItem {
    name: string
    count: number
    sum: number
    countShare: number
    sumShare: number
    color: string
}

export interface ReportBreakdownProps {
    title: string
    items: ReportBreakdownItem[]
    countLabel: string
    formatCount: (value: number) => string
    formatSum: (value: number) => string
    renderIcon?: (name: string) => React.ReactNode
}

const percent = (share: number) => `${Math.round(share * 1000) / 10}%`

/** Desglose por categoría: barra apilada por importe y, por fila, conteo e importe con su participación. */
const ReportBreakdown = ({ title, items, countLabel, formatCount, formatSum, renderIcon }: ReportBreakdownProps) => (
    <section className="k-card">
        <h2 className="k-card-title">{title}</h2>
        {items.length === 0 ? (
            <p className="m-0 text-sm text-k-muted">Sin datos para los filtros aplicados.</p>
        ) : (
            <>
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-k-line-soft" role="img" aria-label={`${title}: participación por importe`}>
                    {items.map((item) => (
                        <span key={item.name} title={`${item.name} · ${percent(item.sumShare)}`} style={{ width: `${item.sumShare * 100}%`, background: item.color }} />
                    ))}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                                <th className="py-2 pr-3 font-bold">Categoría</th>
                                <th className="px-3 py-2 text-right font-bold">{countLabel}</th>
                                <th className="py-2 pl-3 text-right font-bold">Prima total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.name} className="border-t border-k-line-soft">
                                    <td className="py-2.5 pr-3">
                                        <span className="flex min-w-0 items-center gap-2.5">
                                            <span className="h-3 w-3 shrink-0 rounded-[4px]" style={{ background: item.color }} />
                                            {renderIcon?.(item.name)}
                                            <span className="truncate font-semibold text-k-ink">{item.name}</span>
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">
                                        <span className="flex flex-col items-end gap-1">
                                            <span className="text-k-ink">{formatCount(item.count)} <span className="text-xs text-k-muted">· {percent(item.countShare)}</span></span>
                                            <span className="block h-1 w-24 overflow-hidden rounded-full bg-k-line-soft">
                                                <span className="block h-full rounded-full" style={{ width: `${item.countShare * 100}%`, background: item.color }} />
                                            </span>
                                        </span>
                                    </td>
                                    <td className="py-2.5 pl-3 text-right tabular-nums">
                                        <span className="flex flex-col items-end gap-1">
                                            <span className="font-semibold text-k-ink">{formatSum(item.sum)} <span className="text-xs font-normal text-k-muted">· {percent(item.sumShare)}</span></span>
                                            <span className="block h-1 w-24 overflow-hidden rounded-full bg-k-line-soft">
                                                <span className="block h-full rounded-full" style={{ width: `${item.sumShare * 100}%`, background: item.color }} />
                                            </span>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        )}
    </section>
)

export default ReportBreakdown
